import * as z from "zod/v4";
import { customerCustomFieldValueSchema } from "../../customer-custom-fields/customer-custom-fields-definition";
import { CustomerEventType } from "../../customer-event/customer-event-definition";
import { CustomerGroupType } from "../../customers/customer-group-definition";
import { ConjunctionMode } from "../../segments/segment-types";
import { WhereCondition } from "../../segments/segment-where-condition";
import { FullDateTimeframeCondition } from "./segment-timeframe";

export interface Segment {
  name: string;
  type: CustomerGroupType;
  lastUpdated: Date;
  mode: ConjunctionMode;
  conditionBlocks: SegmentConditionBlock[];
  initialStatistics: SegmentSubscriberStatistics;
}

export interface SegmentSubscriberStatistics {
  eligibleCustomers: number | undefined;
  emailEligibleCount: number | undefined;
  emailSubscriberCount: number | undefined;
  smsSubscriberCount: number | undefined;
  // subscriberAOV: number;
}

export const segmentMemberSchema = z.object({
  _id: z.string(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  phone_number: z.string().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  created_at: z.date().nullable(),
  updated_at: z.date().nullable(),
  address: z.string().nullable(),
  birthday: z.date().nullable(),
  shopify_id: z.string().nullable(),
  email_subscription_status: z.string().nullable(),
  sms_subscription_status: z.string().nullable(),
  tags: z.string().nullable(),
  custom_fields: z
    .record(z.string(), customerCustomFieldValueSchema)
    .nullable(),
});
export type SegmentMember = z.infer<typeof segmentMemberSchema>;

export const segmentMemberColumnKeys = [
  "name",
  "email",
  "phone_number",
  "first_name",
  "last_name",
  "created_at",
  "updated_at",
  "address",
  "birthday",
  "shopify_id",
  "email_subscription_status",
  "sms_subscription_status",
  "tags",
  "custom_fields",
] as const;
export type SegmentMemberColumnKey = (typeof segmentMemberColumnKeys)[number];

export enum SegmentConditionCategory {
  ACCOUNT = "account",
  MARKETING = "marketing",
  SHOPPING = "shopping",
}

export enum CustomerCharacteristicType {
  // marketing
  SUBSCRIBED_TO_EMAIL = "subscribed-to-email",
  CAN_RECEIVE_EMAIL_MARKETING = "can-receive-email-marketing",
  SUBSCRIBED_TO_SMS = "subscribed-to-sms",
  CREATED_TIME = "created-time",
  CUSTOMER_TAGS = "customer-tags",

  // attributes
  STATIC_SEGMENT_MEMBERSHIP = "static-segment-membership",
  RETURN_RATE_VALUE = "return-rate-value",
  RETURN_RATE_COUNT = "return-rate-count",
  BIRTHDAY = "birthday",
  CUSTOM_FIELDS = "custom-fields",
  PHONE_NUMBER_AREA_CODE = "phone-number-area-code",

  STATE_PROVINCE = "state-province",
  COUNTRY = "country",
  CITY = "city",
  PROXIMITY_TO_CITY = "proximity-to-city",
  // ZIP_POSTAL_CODE = "zip-postal-code",
}

export enum CustomerDataSource {
  REDO = "Redo",
  SHOPIFY = "Shopify",
}

export enum CustomerActivityType {
  // marketing
  RECEIVED_EMAIL = "received-email",
  OPENED_EMAIL = "opened-email",
  CLICKED_EMAIL = "clicked-email",
  RECEIVED_TEXT = "received-text",
  CLICKED_TEXT = "clicked-text",

  // shopping
  VIEWED_PRODUCT = "viewed-product",
  ADDED_PRODUCT_TO_CART = "added-product-to-cart",
  ORDER_PLACED = "order-placed",
  CHECKOUT_STARTED = "checkout-started",
  ACTIVE_ON_SITE = "active-on-site",
  COLLECTION_VIEWED = "collection-viewed",
  RETURN_PROCESSED = "return-processed",
  SUBSCRIBED_TO_BACK_IN_STOCK = "subscribed-to-back-in-stock",
  TRIGGERED_BACK_IN_STOCK_NOTIFICATION = "triggered-back-in-stock-notification",
}

export const customerActivityTypeToCustomerEventType: Record<
  CustomerActivityType,
  CustomerEventType
> = {
  [CustomerActivityType.RECEIVED_EMAIL]: CustomerEventType.OUTREACH_DELIVERY,
  [CustomerActivityType.OPENED_EMAIL]: CustomerEventType.OUTREACH_OPEN,
  [CustomerActivityType.CLICKED_EMAIL]: CustomerEventType.OUTREACH_CLICK,
  [CustomerActivityType.RECEIVED_TEXT]: CustomerEventType.OUTREACH_DELIVERY,
  [CustomerActivityType.CLICKED_TEXT]: CustomerEventType.OUTREACH_CLICK,
  [CustomerActivityType.VIEWED_PRODUCT]: CustomerEventType.PRODUCT_VIEWED,
  [CustomerActivityType.ADDED_PRODUCT_TO_CART]:
    CustomerEventType.PRODUCT_ADDED_TO_CART,
  [CustomerActivityType.ORDER_PLACED]: CustomerEventType.ORDER_CREATED,
  [CustomerActivityType.CHECKOUT_STARTED]: CustomerEventType.CHECKOUT_STARTED,
  [CustomerActivityType.ACTIVE_ON_SITE]: CustomerEventType.STORE_PAGE_VIEWED,
  [CustomerActivityType.COLLECTION_VIEWED]: CustomerEventType.COLLECTION_VIEWED,
  [CustomerActivityType.RETURN_PROCESSED]: CustomerEventType.RETURN_PROCESSED,
  [CustomerActivityType.SUBSCRIBED_TO_BACK_IN_STOCK]:
    CustomerEventType.SUBSCRIBED_TO_BACK_IN_STOCK,
  [CustomerActivityType.TRIGGERED_BACK_IN_STOCK_NOTIFICATION]:
    CustomerEventType.TRIGGERED_BACK_IN_STOCK_NOTIFICATION,
};

export interface SegmentConditionBlock {
  mode: ConjunctionMode;
  conditions: SegmentCondition[];
}

export enum SegmentConditionType {
  CUSTOMER_ATTRIBUTE = "customer_attribute",
  CUSTOMER_ACTIVITY = "customer_activity",
}

export type SegmentCondition =
  | CustomerAttributeCondition
  | CustomerActivityCondition;

export interface CustomerAttributeCondition {
  type: SegmentConditionType.CUSTOMER_ATTRIBUTE;
  whereCondition: WhereCondition<CustomerCharacteristicType>;
}

export enum ActivityCountType {
  AT_LEAST_ONCE = "at_least_once",
  ZERO_TIMES = "zero_times",
  N_TIMES = "n_times",
  NOT_N_TIMES = "not_n_times",
  AT_LEAST_N = "at_least_n",
  GREATER_THAN_N = "greater_than_n",
  LESS_THAN_N = "less_than_n",
  AT_MOST_N = "at_most_n",
}

export function isConstantActivityCountType(
  type: ActivityCountType,
): type is ConstantActivityCount["type"] {
  return (
    type === ActivityCountType.AT_LEAST_ONCE ||
    type === ActivityCountType.ZERO_TIMES
  );
}

export function isConstantActivityCount(activityCount: {
  type: ActivityCountType;
}): activityCount is ConstantActivityCount {
  return isConstantActivityCountType(activityCount.type);
}

export type ConstantActivityCount = {
  type: ActivityCountType.AT_LEAST_ONCE | ActivityCountType.ZERO_TIMES;
};

export type NTimesActivityCount = {
  type: Exclude<
    ActivityCountType,
    ActivityCountType.AT_LEAST_ONCE | ActivityCountType.ZERO_TIMES
  >;
  n: number;
};

export type ActivityCount = ConstantActivityCount | NTimesActivityCount;

export interface CustomerActivityCondition {
  type: SegmentConditionType.CUSTOMER_ACTIVITY;
  activityType: CustomerActivityType;
  count: ActivityCount;
  timeframe: FullDateTimeframeCondition;
  whereConditions: WhereCondition<CustomerActivityType>[];
}
